import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Pencil, Trash2, X, Loader2, Eye, EyeOff, GripVertical } from "lucide-react";
import { toast } from "sonner";

const ICON_OPTIONS = [
  { value: "users",       label: "Users (worshippers, members)" },
  { value: "book",        label: "Book (students, classes)" },
  { value: "hand-heart",  label: "Hand & Heart (charity, meals)" },
  { value: "calendar",    label: "Calendar (events)" },
  { value: "heart",       label: "Heart (donors, support)" },
  { value: "star",        label: "Star (awards, milestones)" },
  { value: "mosque",      label: "Mosque/Building (facility)" },
];

const blank = {
  label: "",
  value: 0,
  suffix: "+",
  icon: "users",
  description: "",
  order: 0,
  is_published: true,
};

export default function ImpactStatsAdmin() {
  const [items, setItems] = useState([]);
  const [headings, setHeadings] = useState({
    impact_eyebrow: "",
    impact_heading: "",
    impact_subheading: "",
  });
  const [loading, setLoading] = useState(true);
  const [savingHead, setSavingHead] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [list, settings] = await Promise.all([
        api.get("/admin/impact-stats"),
        api.get("/settings"),
      ]);
      setItems(list.data || []);
      setHeadings({
        impact_eyebrow: settings.data?.impact_eyebrow || "",
        impact_heading: settings.data?.impact_heading || "",
        impact_subheading: settings.data?.impact_subheading || "",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveHeadings = async () => {
    setSavingHead(true);
    try {
      // /settings PUT merges into site_settings doc
      const r = await api.get("/settings");
      const merged = { ...(r.data || {}), ...headings };
      // Strip server-managed fields that the PUT model doesn't accept
      delete merged._id;
      delete merged.updated_at;
      await api.put("/admin/settings", merged);
      toast.success("Section heading saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save heading");
    } finally {
      setSavingHead(false);
    }
  };

  const startNew = () => setEditing({ ...blank, order: items.length + 1 });
  const startEdit = (it) => setEditing({ ...it });

  const save = async () => {
    if (!editing.label.trim()) return toast.error("Label is required");
    setSaving(true);
    try {
      const payload = {
        label: editing.label.trim(),
        value: Number(editing.value) || 0,
        suffix: editing.suffix ?? "",
        icon: editing.icon || "users",
        description: editing.description || "",
        order: Number(editing.order) || 0,
        is_published: !!editing.is_published,
      };
      if (editing.id) {
        await api.put(`/admin/impact-stats/${editing.id}`, payload);
        toast.success("Stat updated");
      } else {
        await api.post("/admin/impact-stats", payload);
        toast.success("Stat created");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this stat permanently?")) return;
    try {
      await api.delete(`/admin/impact-stats/${id}`);
      toast.success("Deleted");
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleVisible = async (it) => {
    try {
      await api.put(`/admin/impact-stats/${it.id}`, { ...it, is_published: !it.is_published });
      toast.success(!it.is_published ? "Now visible on homepage" : "Hidden from homepage");
      await load();
    } catch {
      toast.error("Could not update visibility");
    }
  };

  return (
    <div data-testid="admin-impact-stats-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Manage</div>
        <h1 className="font-serif text-4xl mt-2 text-ink">Impact Stats</h1>
        <p className="mt-2 text-ink/60 text-sm max-w-2xl">
          Numbers that animate on the homepage "Our community, in numbers" strip. Edit the heading
          and any individual stat — changes appear instantly to public visitors.
        </p>
      </div>

      {/* HEADING EDITOR */}
      <div className="rounded-3xl bg-white border border-sand p-6 md:p-8 mb-10">
        <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold mb-1">Section heading</div>
        <h2 className="font-serif text-2xl text-ink">What appears above the numbers</h2>
        <div className="grid md:grid-cols-3 gap-4 mt-5">
          <Field
            label="Eyebrow (small uppercase line)"
            value={headings.impact_eyebrow}
            onChange={(v) => setHeadings({ ...headings, impact_eyebrow: v })}
            testId="impact-eyebrow"
          />
          <Field
            label="Heading"
            value={headings.impact_heading}
            onChange={(v) => setHeadings({ ...headings, impact_heading: v })}
            testId="impact-heading"
          />
          <Field
            label="Subheading"
            value={headings.impact_subheading}
            onChange={(v) => setHeadings({ ...headings, impact_subheading: v })}
            testId="impact-subheading"
            multiline
          />
        </div>
        <div className="mt-5 flex justify-end">
          <button
            onClick={saveHeadings}
            disabled={savingHead}
            data-testid="impact-save-headings"
            className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-2.5 text-sm font-semibold hover:bg-navy-900 disabled:opacity-60"
          >
            {savingHead && <Loader2 size={14} className="animate-spin" />}
            Save heading
          </button>
        </div>
      </div>

      {/* STATS LIST */}
      <div className="flex justify-between items-center mb-5">
        <h2 className="font-serif text-2xl text-ink">Numbers</h2>
        <button
          onClick={startNew}
          data-testid="impact-new-btn"
          className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-2.5 text-sm font-semibold hover:bg-navy-900"
        >
          <Plus size={14} /> Add a stat
        </button>
      </div>

      {loading ? (
        <div className="text-ink/40">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-3xl bg-white border border-sand p-14 text-center text-ink/60">
          No stats yet. Click "Add a stat" to create one.
        </div>
      ) : (
        <div className="rounded-3xl bg-white border border-sand admin-scroll admin-scroll-hint">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-sand/50 text-left">
              <tr>
                <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-ink/60 w-10"></th>
                <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-ink/60">Number</th>
                <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-ink/60">Label</th>
                <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-ink/60">Icon</th>
                <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-ink/60">Order</th>
                <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-ink/60">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-sand hover:bg-cream/60">
                  <td className="px-5 py-3 text-ink/30"><GripVertical size={14} /></td>
                  <td className="px-5 py-3 text-ink font-serif text-xl tabular-nums">
                    {Number(it.value).toLocaleString()}{it.suffix}
                  </td>
                  <td className="px-5 py-3 text-ink/85">
                    <div className="font-medium">{it.label}</div>
                    {it.description && <div className="text-xs text-ink/55 mt-0.5">{it.description}</div>}
                  </td>
                  <td className="px-5 py-3 text-ink/65 text-xs"><code className="bg-sand/40 px-1.5 py-0.5 rounded">{it.icon}</code></td>
                  <td className="px-5 py-3 text-ink/65">{it.order}</td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] uppercase font-semibold rounded-full px-2.5 py-1 ${it.is_published ? "bg-navy/10 text-navy" : "bg-sand text-ink/50"}`}>
                      {it.is_published ? "Live" : "Hidden"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => toggleVisible(it)}
                      data-testid={`impact-toggle-${it.id}`}
                      className="p-2 text-ink/60 hover:text-navy"
                      title={it.is_published ? "Hide from homepage" : "Show on homepage"}
                    >
                      {it.is_published ? <Eye size={14} /> : <EyeOff size={14} />}
                    </button>
                    <button
                      onClick={() => startEdit(it)}
                      data-testid={`impact-edit-${it.id}`}
                      className="p-2 text-ink/60 hover:text-navy"
                      title="Edit"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => del(it.id)}
                      data-testid={`impact-delete-${it.id}`}
                      className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-xl max-h-[92vh] overflow-y-auto rounded-3xl bg-white border border-sand p-7 md:p-10">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl text-ink">{editing.id ? "Edit stat" : "New stat"}</h2>
              <button onClick={() => setEditing(null)} className="p-2 text-ink/60 hover:text-ink"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <Field label="Label *  (e.g. Weekly worshippers)" value={editing.label} onChange={(v) => setEditing({ ...editing, label: v })} testId="impact-fld-label" />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Value (number)" type="number" value={editing.value} onChange={(v) => setEditing({ ...editing, value: v })} testId="impact-fld-value" />
                <Field label="Suffix  (e.g. +, K, %)" value={editing.suffix} onChange={(v) => setEditing({ ...editing, suffix: v })} testId="impact-fld-suffix" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Icon</label>
                <select
                  value={editing.icon || "users"}
                  onChange={(e) => setEditing({ ...editing, icon: e.target.value })}
                  data-testid="impact-fld-icon"
                  className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy bg-white"
                >
                  {ICON_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <Field label="Description  (optional, small caption under the number)" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} testId="impact-fld-desc" multiline />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Order  (low = left)" type="number" value={editing.order} onChange={(v) => setEditing({ ...editing, order: v })} testId="impact-fld-order" />
                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2 text-sm text-ink/80 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!editing.is_published}
                      onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })}
                      data-testid="impact-fld-published"
                      className="w-4 h-4 accent-navy"
                    />
                    Show on homepage
                  </label>
                </div>
              </div>
              <div className="pt-4 flex justify-end gap-3">
                <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full text-ink/70 hover:bg-sand/50 text-sm">Cancel</button>
                <button
                  onClick={save}
                  disabled={saving}
                  data-testid="impact-fld-save"
                  className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-6 py-2.5 text-sm font-semibold hover:bg-navy-900 disabled:opacity-60"
                >
                  {saving && <Loader2 size={14} className="animate-spin" />}
                  {editing.id ? "Save changes" : "Create stat"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId, multiline = false }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">{label}</label>
      {multiline ? (
        <textarea
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          rows={2}
          className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy bg-white text-sm"
        />
      ) : (
        <input
          type={type}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy bg-white"
        />
      )}
    </div>
  );
}
